import {ESBA_DATE_FORMAT, ESBA_DATE_ONLY_FORMAT, LOCAL_DATE_FORMAT} from 'common';
import {addBusinessDays, formatDistanceStrict, isMatch, isValid, parse, parseISO, differenceInBusinessDays} from "date-fns";
import {format, utcToZonedTime} from "date-fns-tz";
import {memo, useEffect, useState} from "react";

export function formatDate(date: Date, esbadateformat = ESBA_DATE_FORMAT) {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return format(utcToZonedTime(date, browserTz),
        esbadateformat,
        {timeZone: browserTz});
}

export function formatISODateStr(dateStr: string, esbadateformat = ESBA_DATE_FORMAT) {
    const date = parseISO(dateStr);
    if (!isValid(date)) {
        return '';
    }

    return formatDate(date, esbadateformat);
}

/**
 * Parses a date string in "yyyy-MM-dd" format and returns "MM/dd/yyyy"
 * Avoids timezone issues by not using Date object
 */
export function formatLocalDateString(dateStr: string): string {
    if (!dateStr) return '';
    // I want to add fail safe for invalid date string
    const date = parseISO(dateStr);
    if (!isValid(date)) {
        return '';
    }
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
}

export function formatLocalDate(date: Date) {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return format(utcToZonedTime(date, browserTz),
        LOCAL_DATE_FORMAT,
        {timeZone: browserTz});
}

export function tryFormatLocalDate(date: Date | null) {
    if (date != null) {
        return formatLocalDate(date);
    }

    return null;
}

export function formatDateStr(dateStr: string, plusBusinessDays?: number) {
    let parsedDate = parse(dateStr, "yyyy-MM-dd", new Date())
    if (isValid(parsedDate)) {
        if (plusBusinessDays) {
            parsedDate = addBusinessDays(parsedDate, plusBusinessDays);
        }
        return formatDate(parsedDate);
    }
    return "";
}

export function formatRangeDateStr(start: string, end: string): string {
    const startDate = parse(start, "yyyy-MM-dd", new Date());
    const endDate = parse(end, "yyyy-MM-dd", new Date());
    if (isValid(startDate) && isValid(endDate)) {
        return `${formatDate(startDate, ESBA_DATE_ONLY_FORMAT)} to ${formatDate(endDate)}`;
    }

    return "";
}

export function formatDateWithValidate(date: Date) {
    if (isValidDate(date)) {
        return formatDate(date);
    }

    return "";
}

export function isValidDate(date: Date) {
  return !isNaN(date.getTime());
}

export function isoDateStringToLocalDateFormat(dateISOStr: string) {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return format(parseISO(dateISOStr), LOCAL_DATE_FORMAT, {timeZone: browserTz});
}

export function timeAgo(dateISOStr: string): string {
    return formatDistanceStrict(new Date(dateISOStr), Date.now(), {addSuffix: true});
}

type TimeAgoCountDownProps = {
    dateISOStr: string
}

export const TimeAgoCountDown = memo(({dateISOStr}: TimeAgoCountDownProps) => {
    const parsed = parseISO(dateISOStr);
    const [distance, setDistance] = useState(formatDistanceStrict(parsed, Date.now(), {addSuffix: true}));
    let defaultTimeInterval = 1000; // 1s

    if (Date.now() - parsed.getTime() > 60 * 1000) {
        defaultTimeInterval = 60 * 1000; // 60s
    }

    if (Date.now() - parsed.getTime() > 60 * 60 * 1000) {
        defaultTimeInterval = 60 * 60 * 1000; // 1 hour
    }

    useEffect(
        () => {
            const id = setTimeout(() => {
                setDistance(formatDistanceStrict(parsed, Date.now(), {addSuffix: true}));
            }, defaultTimeInterval);
            return () => {
                clearTimeout(id);
            };
        },
        [defaultTimeInterval, parsed],
    );

    return <div>
        {distance}
    </div>
});

/**
 * Converts a form date value to yyyy-MM-dd for date-only API fields.
 * Accepts yyyy-MM-dd passthrough or an ISO instant from the date picker.
 */
export function toDateOnlyString(value: string | null | undefined): string | null {
    if (value == null || value === '') {
        return value ?? null;
    }
    if (isMatch(value, LOCAL_DATE_FORMAT)) {
        return value;
    }
    const date = new Date(value);
    if (!isValid(date)) {
        return null;
    }
    return format(date, LOCAL_DATE_FORMAT);
}

/**
 * Converts a user-selected date to Los Angeles timezone ISO string for backend
 * This ensures the selected date is interpreted as a date in Los Angeles timezone
 * @param date - The Date object selected by the user
 * @returns ISO string representing the date at start of day in Los Angeles timezone
 */
export function toBackendDateISO(date: Date | null): string | null {
    if (!date) return null;
    
    // Get the local date components from user selection
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    // Create a date string in YYYY-MM-DD format
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Simple approach: create the date as if it's midnight in Los Angeles
    // and let the backend handle the timezone interpretation
    return `${dateStr}T08:00:00.000Z`; // 8 AM UTC = midnight PST (UTC-8)
}

/**
 * Parses a backend ISO date string and returns a Date object for display
 * @param isoString - ISO string from backend (in Los Angeles timezone context)
 * @returns Date object for date picker display
 */
export function parseBackendDateISO(isoString: string | null): Date | null {
    if (!isoString) return null;
    
    try {
        // Extract just the date part from the ISO string
        const datePart = isoString.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        
        // Create a date object representing this calendar date
        return new Date(year, month - 1, day);
    } catch {
        return null;
    }
}

export const validateYear = (shouldValidate: boolean) => {
    if (!shouldValidate) {
        return (_: string | null) => true;
    }

    return (value: string | null) => {
        if (value === null) {
            return true;
        }

        const date = parseISO(value);
        const year = date.getFullYear();
        if (year > 9999) {
            return "Invalid Date";
        }
        return true;
    }
};

/**
 * Calculates the difference in business days between two ISO strings representing dates in the local timezone.
 * Returns positive if dateLeft is after dateRight.
 */
export function differenceInBusinessDaysLocal(
  dateLeftStr: string,
  dateRightStr: string,
): number {
  const dateLeft = parseISO(dateLeftStr);
  const dateRight = parseISO(dateRightStr);

  if (!isValid(dateLeft) || !isValid(dateRight)) {
    return 0;
  }

  return differenceInBusinessDays(dateLeft, dateRight);
}
